import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import {
  InfoIcon,
  WarningIcon,
  WarningOctagonIcon,
  XIcon,
  type Icon,
} from '@phosphor-icons/react';
import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI';
import type {
  PortalNoticeState,
  PortalNoticeType,
} from 'volto-ministerio-publico-federal/reducers/portalNotice';

export const PORTAL_NOTICE_STORAGE_PREFIX = 'mpf_portal_notice_';

const ICONS: Record<PortalNoticeType, Icon> = {
  info: InfoIcon,
  warning: WarningIcon,
  critical: WarningOctagonIcon,
};

const readDismissed = (storageKey: string) => {
  try {
    return localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
};

export const PortalNotice: React.FC = () => {
  const notice = useSelector(
    (state: { portalNotice?: PortalNoticeState }) => state.portalNotice?.data,
  );
  const isCritical = notice?.type === 'critical';
  const storageKey = notice
    ? `${PORTAL_NOTICE_STORAGE_PREFIX}${notice.key}`
    : '';
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (storageKey) setDismissed(readDismissed(storageKey));
  }, [storageKey]);

  if (!notice || (!isCritical && dismissed)) return null;

  const type: PortalNoticeType = ICONS[notice.type] ? notice.type : 'warning';
  const TypeIcon = ICONS[type];

  const handleClose = () => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {}
    setDismissed(true);
  };

  return (
    <section
      role={isCritical ? 'alert' : 'status'}
      aria-live={isCritical ? 'assertive' : 'polite'}
      aria-label="Aviso"
      className={cx('mpf-portal-notice', `mpf-portal-notice--${type}`)}
    >
      <div className="mpf-portal-notice__box">
        <TypeIcon
          aria-hidden="true"
          weight="fill"
          className="mpf-portal-notice__icon"
        />
        <div className="mpf-portal-notice__content">
          {notice.title && (
            <p className="mpf-portal-notice__title">{notice.title}</p>
          )}
          {notice.text && (
            <div
              className="mpf-portal-notice__text"
              dangerouslySetInnerHTML={{ __html: notice.text }}
            />
          )}
        </div>
        {!isCritical && (
          <MPFIconButton
            variant="unstyled"
            onClick={handleClose}
            aria-label="Fechar aviso"
            className="mpf-portal-notice__close"
            icon={XIcon}
          />
        )}
      </div>
    </section>
  );
};

export default PortalNotice;
